import { ReactNode } from 'react';
import { cn } from '@crisellevaldez/speculo-ui';
import { ArrowDownLeft, ArrowRight, ArrowUpRight } from 'lucide-react';
import { previewEmail } from '../../../../utils/email/previewEmail';
import { formatDate } from '../../../../utils/dateTime';
import { avatarInitial, avatarTint } from '../../../../utils/avatar';
import { CrmStageDisplay } from '../../../common/CrmStageDisplay';
import { ThreadCount } from '../../../common/ThreadCount';
import { emailLeadAddress } from '../../../../utils/email/emailLeadAddress';
import type { CrmStageProjection } from '../../../../types/crmStage.types';

/** A Remi conversation row in an email drill-down: opens as a thread. */
export interface EmailThreadSelection {
  conversationId: string | null;
  messageId: string;
  contactId: string | null;
  contactName: string | null;
  contactEmail: string | null;
}

/**
 * Shared column factory for the Emails / Email Opens / Email Clicks
 * drill-downs, laid out like the SMS drill-down: avatar, name and time,
 * a direction-arrow preview line, and a muted footer (CRM stage · email
 * address · email count) whose action opens the email. Remi's conversations come from the backend as one row each
 * (their latest email) and open the whole thread; eAlert and
 * flow-template rows open that one email. A click elsewhere on the row
 * opens the contact (the modal's `onRowClick`).
 */
export function buildEmailColumns(opts: {
  timezone?: string;
  onOpenContact: (contactId: string) => void;
  onOpenThread: (thread: EmailThreadSelection) => void;
  /** From `useShowCrmStage`; the footer leads with the CRM stage when set. */
  showCrmStage?: boolean;
}): Array<{
  key: string;
  header: string;
  width?: string;
  cell: (item: Record<string, unknown>) => ReactNode;
}> {
  const { timezone, onOpenContact, onOpenThread, showCrmStage } = opts;
  return [
    {
      key: 'email',
      header: 'Emails',
      cell: (item) => {
        const name = item.contactName as string | undefined;
        const contactId = item.contactId as string | undefined;
        const displayName = name && name !== 'unknown' ? name : null;
        const isInbound = (item.direction as string | undefined)?.toLowerCase() === 'inbound';
        const address =
          emailLeadAddress(
            item.direction as string | null,
            item.fromAddress as string | null,
            item.toAddress as string | null,
          ) ?? undefined;
        const subject = item.subject as string | undefined;
        const body = ((item.replyText ?? item.body) as string | undefined) || '';
        const html = item.bodyHtml as string | null;
        const ts = item.sentAt as string | undefined;
        const template = item.templateName as string | undefined;
        const isThread = item.emailKind === 'AGENTIC';
        const count = (item.threadMessageCount as number | null | undefined) ?? 1;
        const crmStages = item.crmStages as CrmStageProjection[] | undefined;
        // A conversation previews its latest email; an eAlert previews its subject.
        const preview = isThread ? body || subject || '' : subject || body || '';

        const openThread = () =>
          onOpenThread({
            conversationId: (item.conversationId as string | null) ?? null,
            messageId: item.messageId as string,
            contactId: contactId ?? null,
            contactName: displayName,
            contactEmail: address ?? null,
          });
        const openEmail = html
          ? () =>
              previewEmail({
                bodyHtml: html,
                subject,
                contactName: displayName ?? undefined,
                contactEmail: address,
                toAddress: address,
                sentAt: ts,
                templateName: template,
                direction: 'outbound',
              })
          : null;

        return (
          <div className="group flex items-start gap-2.5 sm:gap-3 py-1.5 min-w-0 cursor-pointer">
            <div
              className={cn(
                'flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold ring-2',
                avatarTint(contactId || displayName || address),
              )}
            >
              {avatarInitial(displayName ?? address)}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                {contactId && displayName ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenContact(contactId);
                    }}
                    className="text-primaryfortext font-semibold underline text-left truncate"
                  >
                    {displayName}
                  </button>
                ) : (
                  <span className="font-semibold text-foreground truncate">
                    {displayName ?? address ?? '—'}
                  </span>
                )}
                {ts && (
                  <span className="text-muted-foreground whitespace-nowrap flex-shrink-0">
                    {formatDate(ts, timezone)}
                  </span>
                )}
              </div>

              {preview && (
                <div className="flex items-start gap-1.5 sm:gap-2 mt-1 text-foreground/90">
                  {isInbound ? (
                    <ArrowDownLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4 call-color-inbound flex-shrink-0 mt-0.5" />
                  ) : (
                    <ArrowUpRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 call-color-outbound flex-shrink-0 mt-0.5" />
                  )}
                  {!isThread && openEmail ? (
                    // An eAlert's subject opens the email, as in the earlier subject column.
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEmail();
                      }}
                      className="block min-w-0 max-w-[1000px] truncate text-left text-primaryfortext underline decoration-dotted underline-offset-2 hover:text-primary/80"
                      title={preview}
                    >
                      {preview}
                    </button>
                  ) : (
                    <span className="block min-w-0 max-w-[1000px] truncate" title={preview}>
                      {preview}
                    </span>
                  )}
                </div>
              )}

              <div className="flex items-center gap-1.5 sm:gap-2 mt-1.5 text-muted-foreground flex-wrap min-w-0">
                {showCrmStage && (
                  <>
                    <CrmStageDisplay stages={crmStages} compact />
                    <span>·</span>
                  </>
                )}
                {address && (
                  <a
                    href={`mailto:${address}`}
                    onClick={(e) => e.stopPropagation()}
                    className="truncate max-w-[260px] hover:text-primaryfortext hover:underline"
                  >
                    {address}
                  </a>
                )}
                {isThread && (
                  <>
                    {address && <span>·</span>}
                    <ThreadCount count={count} channel="email" />
                  </>
                )}
                {(isThread || openEmail) && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isThread) openThread();
                      else openEmail?.();
                    }}
                    className="inline-flex items-center gap-1 text-primaryfortext font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity hover:underline"
                  >
                    {isThread ? 'Open thread' : 'Preview email'}
                    <ArrowRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
  ];
}
